import type { MouseEvent } from 'react';
import type { OutreachRow } from '@discovr/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { DotsThree, MinusCircle, Warning } from '../../../design-system/icons';
import { CRM_ICON_BUTTON } from './CrmCard';
import { creatorName } from './crmCreatorColumns';
import { crmSuppressionAction, type CrmSuppressionAction } from './crmSuppression';
import type { CrmSuppression } from './useCrmSuppression';

interface CrmCreatorRowMenuProps {
  row: OutreachRow;
  suppression: CrmSuppression;
}

/** Keeps a menu click from also opening the row's conversation. */
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

/** The All creators row menu: Suppress / Unsuppress, only for people allowed to change suppressions. */
export function CrmCreatorRowMenu({ row, suppression }: CrmCreatorRowMenuProps) {
  const action = crmSuppressionAction(row);
  if (!suppression.canManage || action.kind === 'none') return null;
  return (
    <div className="flex justify-end" onClick={stopRowClick}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label={`Actions for ${creatorName(row)}`} className={CRM_ICON_BUTTON}>
            <DotsThree size={20} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <SuppressionItem row={row} action={action} suppression={suppression} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface SuppressionItemProps {
  row: OutreachRow;
  action: Exclude<CrmSuppressionAction, { kind: 'none' }>;
  suppression: CrmSuppression;
}

function SuppressionItem({ row, action, suppression }: SuppressionItemProps) {
  if (action.kind === 'unsuppress') {
    return (
      <DropdownMenuItem onSelect={() => suppression.unsuppress(action.suppressionId)}>
        <MinusCircle size={14} />
        Unsuppress
      </DropdownMenuItem>
    );
  }
  if (action.kind === 'suppress_unavailable') {
    return (
      <DropdownMenuItem disabled>
        <Warning size={14} />
        Suppress
        <span className="ml-auto pl-3 text-xs text-muted-foreground">{action.reason}</span>
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem onSelect={() => suppression.requestSuppress(row.entryId, action.address)}>
      <Warning size={14} />
      Suppress
    </DropdownMenuItem>
  );
}
